import { MouseEvent } from 'react';
import { Broadcaster, BroadcasterProps } from '@stadionhq-v3/football-ui';
import { Broadcaster as PulseliveBroadcaster } from '@stadionhq-v3/pulselive-api';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import {
  Asset,
  Broadcaster as ContentfulBroadcaster,
  Link as ContentfulLink,
} from '@graphql/index';
import { Locale } from '@core/constants/locales.constants';
import { Link } from '@stadionhq-v3/base-ui';
import { linkTransform, assetTransform } from '../atoms';

// Prevent click events from bubbling up to parent card link
const handleBroadcasterClick = (e: MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation();
};

const isContentfulBroadcaster = (
  broadcasterType: PulseliveBroadcaster | ContentfulBroadcaster
): broadcasterType is ContentfulBroadcaster => '__typename' in broadcasterType;

const broadcastersTransform = (
  broadcasters: Array<ContentfulBroadcaster | PulseliveBroadcaster>,
  imageBaseUrl: string,
  imageExtension: string,
  locale: Locale
): BroadcasterProps[] => {
  const transformContentfulBroadcaster = (
    broadcaster: ContentfulBroadcaster
  ): BroadcasterProps => {
    const broadcasterLink =
      broadcaster?.link as CleanContentful<ContentfulLink>;

    const link = broadcasterLink?.url
      ? linkTransform(broadcasterLink, locale)
      : undefined;

    const logoAsset = assetTransform(
      broadcaster.logo as CleanContentful<Asset>
    );

    return {
      children: broadcaster.logo ? (
        <Broadcaster>
          {link ? (
            <Link {...link} onClick={handleBroadcasterClick}>
              <img src={logoAsset.url} alt={`${broadcaster.name} Logo`} />
            </Link>
          ) : (
            <img src={logoAsset.url} alt={`${broadcaster.name} Logo`} />
          )}
        </Broadcaster>
      ) : null,
    };
  };

  const transformPulseliveBroadcaster = (
    broadcaster: PulseliveBroadcaster
  ): BroadcasterProps => ({
    children: broadcaster.url ? (
      /* eslint-disable jsx-a11y/anchor-is-valid */
      <Link
        url={broadcaster.url}
        openInNewTab
        title={broadcaster.name}
        onClick={handleBroadcasterClick}
      >
        <Broadcaster>
          {imageBaseUrl && imageExtension && (
            <img
              src={`${imageBaseUrl}${broadcaster.abbreviation.toLowerCase()}.${imageExtension}`}
              alt={broadcaster.name}
            />
          )}
        </Broadcaster>
      </Link>
    ) : (
      <></>
    ),
  });

  return (
    broadcasters?.map((broadcaster) => {
      if (isContentfulBroadcaster(broadcaster)) {
        return transformContentfulBroadcaster(broadcaster);
      }

      return transformPulseliveBroadcaster(broadcaster as PulseliveBroadcaster);
    }) ?? []
  );
};

export default broadcastersTransform;
